import React, { useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./AnnouncementManagementPage.css";

const FALLBACK_CATEGORY_OPTIONS = [
  { _id: "general", title: "General" },
  { _id: "trading", title: "Trading" },
  { _id: "security", title: "Security" },
  { _id: "launchpad", title: "Launchpad" },
  { _id: "maintenance", title: "Maintenance" },
];

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const AnnouncementManagementPage = () => {
  const [activeView, setActiveView] = useState("create"); // create | list
  const [announcements, setAnnouncements] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState("");
  const [form, setForm] = useState({ category: "", title: "", description: "" });
  const [publishLoading, setPublishLoading] = useState(false);
  const [publishMessage, setPublishMessage] = useState({ type: "", text: "" });
  const [statusUpdatingId, setStatusUpdatingId] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ title: "", description: "" });
  const [categorySubmitting, setCategorySubmitting] = useState(false);
  const [categoryMessage, setCategoryMessage] = useState({ type: "", text: "" });
  const [categoryOptions, setCategoryOptions] = useState(FALLBACK_CATEGORY_OPTIONS);

  const fetchCategoryListing = async () => {
    try {
      const res = await AdminService.getAnnouncementCategoryListing();
      if (res?.success && Array.isArray(res.data)) {
        const categories = res.data
          .map((x) => ({
            _id: String(x?._id ?? "").trim(),
            title: String(x?.title ?? "").trim(),
          }))
          .filter((x) => x._id && x.title);
        if (categories.length) {
          setCategoryOptions(categories);
          return;
        }
      }
      setCategoryOptions(FALLBACK_CATEGORY_OPTIONS);
    } catch {
      setCategoryOptions(FALLBACK_CATEGORY_OPTIONS);
    }
  };

  React.useEffect(() => {
    fetchCategoryListing();
  }, []);

  const fetchAnnouncementList = async () => {
    setListLoading(true);
    setListError("");
    try {
      const res = await AdminService.getAllAnnouncementListAdmin();
      if (res?.success && Array.isArray(res.data)) {
        const mapped = res.data.map((x) => ({
          _id: x?._id ?? `ann_${Date.now()}`,
          category: x?.announcementCategory ?? "—",
          title: x?.title ?? "—",
          description: x?.description ?? "—",
          createdAt: x?.createdAt ?? "",
          status: String(x?.status ?? "ACTIVE").toUpperCase(),
        }));
        setAnnouncements(mapped);
      } else {
        setAnnouncements([]);
        setListError(res?.message || "Failed to load announcements");
      }
    } catch {
      setAnnouncements([]);
      setListError("Failed to load announcements");
    } finally {
      setListLoading(false);
    }
  };

  React.useEffect(() => {
    fetchAnnouncementList();
  }, []);

  const counts = {
    total: announcements.length,
    active: announcements.filter((a) => (a.status || "").toLowerCase() === "active").length,
    inactive: announcements.filter((a) => (a.status || "").toLowerCase() === "inactive").length,
  };

  const updateForm = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handlePublish = async (e) => {
    e.preventDefault();
    const title = form.title.trim();
    const description = form.description.trim();
    const announcementCategoryId = form.category;
    if (!title || !description || !announcementCategoryId) return;
    setPublishLoading(true);
    setPublishMessage({ type: "", text: "" });
    try {
      const res = await AdminService.createNewAnnouncement({
        title,
        description,
        announcementCategoryId,
      });
      if (res?.success) {
        const selectedCategory = categoryOptions.find((c) => c._id === announcementCategoryId);
        const newAnn = {
          _id: "ann_" + Date.now(),
          category: selectedCategory?.title || "—",
          title,
          description,
          createdAt: new Date().toISOString(),
          status: "Active",
        };
        setAnnouncements((prev) => [newAnn, ...prev]);
        setForm({ category: "", title: "", description: "" });
        setPublishMessage({ type: "success", text: res?.message || "Announcement created successfully" });
        await fetchAnnouncementList();
      } else {
        setPublishMessage({ type: "error", text: res?.message || "Failed to create announcement" });
      }
    } catch {
      setPublishMessage({ type: "error", text: "Failed to create announcement" });
    } finally {
      setPublishLoading(false);
    }
  };

  const setStatus = async (id, status) => {
    setStatusUpdatingId(id);
    try {
      const res = await AdminService.updateAnnouncementStatus(id, status);
      if (res?.success) {
        setAnnouncements((prev) => prev.map((a) => (a._id === id ? { ...a, status } : a)));
      } else {
        setListError(res?.message || "Failed to update announcement status");
      }
    } catch {
      setListError("Failed to update announcement status");
    } finally {
      setStatusUpdatingId("");
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete this announcement?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });
    if (!result.isConfirmed) return;
    setDeletingId(id);
    try {
      const res = await AdminService.deleteAnnouncement(id);
      if (res?.success) {
        setAnnouncements((prev) => prev.filter((a) => a._id !== id));
        Swal.fire({
          icon: "success",
          title: "Deleted",
          text: res?.message || "Announcement deleted successfully",
          timer: 1400,
          showConfirmButton: false,
        });
      } else {
        setListError(res?.message || "Failed to delete announcement");
      }
    } catch {
      setListError("Failed to delete announcement");
    } finally {
      setDeletingId("");
    }
  };

  const openCategoryModal = () => {
    setCategoryForm({ title: "", description: "" });
    setCategoryMessage({ type: "", text: "" });
    setCategoryModalOpen(true);
  };

  const closeCategoryModal = () => {
    if (categorySubmitting) return;
    setCategoryModalOpen(false);
  };

  const submitCategory = async (e) => {
    e.preventDefault();
    const title = categoryForm.title.trim();
    const description = categoryForm.description.trim();
    if (!title || !description) return;
    setCategorySubmitting(true);
    setCategoryMessage({ type: "", text: "" });
    try {
      const res = await AdminService.addAnnouncementCategory({ title, description });
      if (res?.success) {
        setCategoryMessage({ type: "success", text: res?.message || "Category created successfully" });
        await fetchCategoryListing();
        setTimeout(() => {
          setCategoryModalOpen(false);
          setCategoryMessage({ type: "", text: "" });
        }, 700);
      } else {
        setCategoryMessage({ type: "error", text: res?.message || "Failed to create category" });
      }
    } catch {
      setCategoryMessage({ type: "error", text: "Failed to create category" });
    } finally {
      setCategorySubmitting(false);
    }
  };

  return (
    <main className="dashboard-main dashboard-subpage announcement-page">
      <section className="dashboard-hero announcement-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Announcement</span>
          </p>
          <h1 className="dashboard-hero__title">Announcement Management</h1>
          <p className="dashboard-hero__subtitle">Create and manage platform announcements</p>
        </div>
        <div className="announcement-hero__icon">
          <i className="fas fa-bullhorn" />
        </div>
      </section>

      <div className="dashboard-body">
        <div className="announcement-cards">
          <div className="announcement-card">
            <span className="announcement-card__value">{counts.total}</span>
            <span className="announcement-card__label">Total Announcements</span>
          </div>
          <div className="announcement-card announcement-card--active">
            <span className="announcement-card__value">{counts.active}</span>
            <span className="announcement-card__label">Active</span>
          </div>
          <div className="announcement-card announcement-card--inactive">
            <span className="announcement-card__value">{counts.inactive}</span>
            <span className="announcement-card__label">Inactive</span>
          </div>
        </div>

        <div className="announcement-tabs">
          <button
            type="button"
            className={`announcement-tab announcement-tab--create ${activeView === "create" ? "announcement-tab--active" : ""}`}
            onClick={() => setActiveView("create")}
          >
            <i className="fas fa-plus" /> Create Announcement
          </button>
          <button
            type="button"
            className="announcement-btn announcement-btn--category"
            onClick={openCategoryModal}
          >
            <i className="fas fa-folder-plus" /> Create Category
          </button>
          <button
            type="button"
            className={`announcement-tab announcement-tab--list ${activeView === "list" ? "announcement-tab--active" : ""}`}
            onClick={() => setActiveView("list")}
          >
            <i className="fas fa-list" /> Announcement List
          </button>
        </div>

        <div className="announcement-content">
          {activeView === "create" && (
            <section className="announcement-section">
              <h2 className="announcement-section__title"><i className="fas fa-pen" /> Create New Announcement</h2>
              <form className="announcement-form" onSubmit={handlePublish}>
                <div className="announcement-form__group">
                  <label>Announcement Category <span className="required">*</span></label>
                  <select value={form.category} onChange={(e) => updateForm("category", e.target.value)} required>
                    <option value="">Select Category</option>
                    {categoryOptions.map((c) => <option key={c._id} value={c._id}>{c.title}</option>)}
                  </select>
                </div>
                <div className="announcement-form__group">
                  <label>Announcement Title <span className="required">*</span></label>
                  <input type="text" placeholder="Enter Announcement Title" value={form.title} onChange={(e) => updateForm("title", e.target.value)} required />
                </div>
                <div className="announcement-form__group">
                  <label>Description <span className="required">*</span></label>
                  <textarea placeholder="Write your description..." value={form.description} onChange={(e) => updateForm("description", e.target.value)} rows={6} required />
                </div>
                <div className="announcement-form__actions">
                  <button type="submit" className="announcement-btn announcement-btn--publish" disabled={publishLoading}>
                    <i className={`fas ${publishLoading ? "fa-spinner fa-spin" : "fa-paper-plane"}`} /> {publishLoading ? "Publishing..." : "Publish Announcement"}
                  </button>
                </div>
                {publishMessage.text && (
                  <p className={`announcement-modal__msg announcement-modal__msg--${publishMessage.type}`}>{publishMessage.text}</p>
                )}
              </form>
            </section>
          )}

          {activeView === "list" && (
            <section className="announcement-section">
              <h2 className="announcement-section__title"><i className="fas fa-list" /> Announcement List</h2>
              {listError && <p className="announcement-modal__msg announcement-modal__msg--error">{listError}</p>}
              <div className="announcement-table-wrap">
                <table className="announcement-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Category</th>
                      {/* <th>Description</th> */}
                      <th>Created</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listLoading ? (
                      <tr>
                        <td colSpan="6" className="announcement-table__desc"><i className="fas fa-spinner fa-spin" /> Loading...</td>
                      </tr>
                    ) : (
                      announcements.map((row) => (
                      <tr key={row._id}>
                        <td><strong>{row.title}</strong></td>
                        <td>{row.category}</td>
                        {/* <td className="announcement-table__desc">{row.description}</td> */}
                        <td>{formatDate(row.createdAt)}</td>
                        <td><span className={`announcement-status announcement-status--${(row.status || "").toLowerCase()}`}>{row.status}</span></td>
                        <td>
                          <div className="announcement-actions">
                            <button type="button" className="announcement-action-btn announcement-action-btn--active" onClick={() => setStatus(row._id, "ACTIVE")} title="Set Active" disabled={statusUpdatingId === row._id}>
                              Active
                            </button>
                            <button type="button" className="announcement-action-btn announcement-action-btn--inactive" onClick={() => setStatus(row._id, "INACTIVE")} title="Set Inactive" disabled={statusUpdatingId === row._id}>
                              Inactive
                            </button>
                            <button type="button" className="announcement-action-btn announcement-action-btn--delete" onClick={() => handleDelete(row._id)} title="Delete" disabled={deletingId === row._id}>
                              <i className="fas fa-trash-alt" /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      </div>

      {categoryModalOpen && (
        <div className="announcement-modal-overlay" onClick={closeCategoryModal}>
          <div className="announcement-modal" onClick={(e) => e.stopPropagation()}>
            <div className="announcement-modal__head">
              <h3>Create Category</h3>
              <button type="button" className="announcement-modal__close" onClick={closeCategoryModal} aria-label="Close">×</button>
            </div>
            <form className="announcement-modal__body" onSubmit={submitCategory}>
              <label>
                Title <span className="required">*</span>
                <input
                  type="text"
                  value={categoryForm.title}
                  onChange={(e) => setCategoryForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Launchpad"
                  required
                />
              </label>
              <label>
                Description <span className="required">*</span>
                <textarea
                  rows={4}
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Launchpad"
                  required
                />
              </label>
              {categoryMessage.text && (
                <p className={`announcement-modal__msg announcement-modal__msg--${categoryMessage.type}`}>{categoryMessage.text}</p>
              )}
              <div className="announcement-modal__actions">
                <button type="button" onClick={closeCategoryModal} disabled={categorySubmitting}>Cancel</button>
                <button type="submit" disabled={categorySubmitting}>
                  {categorySubmitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
};

export default AnnouncementManagementPage;
